import { RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PepiconsPopDotsY } from '../../../../../../assets/icon/iconify';
import { Spinner } from '../../../../../../assets/icon/svg';
import { useGetCommentAttachmentMutation } from '../../../../../../api/gms/proposal-chat/proposal-chats-api';
import { GroupChat } from '../../../../../../api/gms/proposal-chat/proposal-chats-api-state';
import { setCommentId, setConfirmTypeModal } from '../../../../../../features/gms-state-and-modal/modal-slice';
import { selectMessageAttachmentData, setMessageAttachmentValue } from '../../../../../../features/chats/chats-slice';
import { formatDateTimeChatMessage } from '../../../../../../utils/format-date-time-message.utils';
import { MessageType } from '../../../../../../types/type';
import { getFileIcon, isImageFile } from '../../../../../ui/get-file-icon';
import { ChatAttachment } from './chat-attachment-modal';
import MessageHeader from './message-header';
import { useToastify } from '../../../../../../library/toastify/toast-provider';

const VIEWABLE_FILE_EXTENSIONS = [
    'pdf',
    'txt',
    'csv',
    'json',
    'xml',
    'md',
    'log',
    'yaml',
    'yml',
    'mp3',
    'wav',
    'ogg',
    'm4a',
    'aac',
    'flac',
    'mp4',
    'webm',
    'mov',
];

const getFileExtension = (fileName = '') => fileName.split('.').pop()?.toLowerCase() ?? '';
export const isViewableFile = (fileName = '') => VIEWABLE_FILE_EXTENSIONS.includes(getFileExtension(fileName));

interface Props {
    messageBgColor?: string;
    styleDiv?: string;
    type: MessageType;
    isActive?: boolean;
    onToggle?: () => void;
    ref?: RefObject<HTMLDivElement | null>;
    setMessage: (value: string) => void;
    msg: GroupChat;
    attachment?: ChatAttachment | null;
    isDeleting: boolean;
    isLoadingDeleteComment: boolean;
    onDownloadAttachment?: (attachment: ChatAttachment) => void;
    isDownloadingAttachment?: boolean;
}

export default function MessageChat({
    isLoadingDeleteComment,
    isDeleting,
    setMessage,
    msg,
    attachment,
    ref,
    isActive,
    onToggle,
    type,
    messageBgColor = 'bg-gray-200',
    styleDiv = 'justify-start',
    onDownloadAttachment,
    isDownloadingAttachment = false,
}: Props) {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const [getCommentAttachment] = useGetCommentAttachmentMutation();
    const {
        imageUrl,
        isLoadingImage,
        isLoadingFile,
    } = useSelector(selectMessageAttachmentData);
    const formattedCreatedAt = formatDateTimeChatMessage(msg.created_at);
    const isEdited = Boolean(msg.updated_at) && msg.updated_at !== msg.created_at;
    const isDeleteMessage = msg.is_deleted === true;
    const isDeletingMessage = isDeleting || isLoadingDeleteComment;
    const hasMessage = Boolean(msg.comment_text?.trim());
    const hasAttachment = Boolean(attachment);
    const hasMessageAndAttachment = hasMessage && hasAttachment;
    const isImageAttachment = Boolean(attachment && isImageFile(attachment.name),);
    const isViewableAttachment = Boolean(attachment && isViewableFile(attachment.name),);

    const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!hasMessage || isDeletingMessage) {
            return;
        }

        setMessage(msg.comment_text ?? '');
        dispatch(setCommentId(msg.comment_id));
        dispatch(setConfirmTypeModal('isCancelUpdateMessage'));
        onToggle?.();
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (isDeletingMessage) {
            return;
        }
        dispatch(setCommentId(msg.comment_id));
        onToggle?.();
        dispatch(setConfirmTypeModal('deleteMessage'),);
    };

    useEffect(() => {
        if ( isDeleteMessage || !attachment?.messageId || !isImageAttachment ) {
            dispatch(setMessageAttachmentValue({key: 'imageUrl', value: null,}),);
            dispatch(setMessageAttachmentValue({key: 'isLoadingImage', value: false,}),);
            return;
        }

        let objectUrl: string | null = null;
        let isMounted = true;

        const loadImage = async () => {
            dispatch(setMessageAttachmentValue({key: 'isLoadingImage', value: true,}),);
            try {
                const blob = await getCommentAttachment({commentId: attachment.messageId!}).unwrap();
                if (!isMounted) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                dispatch(setMessageAttachmentValue({key: 'imageUrl', value: objectUrl,}),);
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                showToastNotify('Failed to load image attachment:', 'error')
                dispatch(setMessageAttachmentValue({key: 'imageUrl', value: null,}),);
            } finally {
                if (isMounted) {
                    dispatch(setMessageAttachmentValue({key: 'isLoadingImage', value: false,}),);
                }
            }
        };

        loadImage();
        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [attachment?.messageId, isDeleteMessage, isImageAttachment, getCommentAttachment, dispatch]);

    const handleOpenImagePreview = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (!imageUrl || isLoadingImage || isDeletingMessage) {
            return;
        }

        dispatch(setMessageAttachmentValue({key: 'fileName', value: attachment?.name ?? null}));
        dispatch(setMessageAttachmentValue({key: 'fileType', value: attachment?.type ?? null}));
        dispatch(setMessageAttachmentValue({key: 'isImagePreviewOpen', value: true}));
    };

    const handleOpenFilePreview = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (!attachment?.messageId || !isViewableAttachment || isLoadingFile || isDeletingMessage) {
            return;
        }

        dispatch(setMessageAttachmentValue({key: 'isLoadingFile', value: true}));

        try {
            const blob = await getCommentAttachment({commentId: attachment.messageId}).unwrap();

            const objectUrl = URL.createObjectURL(blob);
            dispatch(setMessageAttachmentValue({key: 'fileUrl', value: objectUrl}));
            dispatch(setMessageAttachmentValue({key: 'fileName',value: attachment.name ?? null}));
            dispatch(setMessageAttachmentValue({key: 'fileType',value: attachment.type ?? null}));
            dispatch(setMessageAttachmentValue({key: 'isFilePreviewOpen',value: true}));
        } catch (error) {
            showToastNotify('Failed to load image attachment:', 'error')
        } finally {
            dispatch(setMessageAttachmentValue({key: 'isLoadingFile', value: false}));
        }
    };

    const handleDownloadAttachment = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if ( !attachment || isDeletingMessage || isDownloadingAttachment ) {
            return;
        }
        onDownloadAttachment?.(attachment);
    };

    const renderAttachment = () => {
        if (!attachment) {
            return null;
        }

        if (isImageAttachment) {
            return (
                <button
                    type="button"
                    disabled={isLoadingImage || isDeletingMessage}
                    onClick={handleOpenImagePreview}
                    className="group relative block max-w-full overflow-hidden rounded-md disabled:cursor-not-allowed disabled:opacity-80"
                    title="View image"
                >
                    {isLoadingImage ? (
                        <div className="flex h-[180px] w-[220px] items-center justify-center sm:h-[200px] sm:w-[240px] md:h-[220px] md:w-[260px]">
                            <Spinner size={18} />
                        </div>
                    ) : imageUrl ? (
                        <>
                            <img
                                src={imageUrl}
                                alt={attachment.name}
                                loading="lazy"
                                className="block max-h-[280px] max-w-[280px] object-contain transition group-hover:scale-[1.01] group-hover:opacity-90 sm:max-h-[320px] sm:max-w-[320px]"
                            />

                            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/50 px-2 py-1.5 opacity-0 transition group-hover:opacity-100">
                                <span className="min-w-0 max-w-[220px] truncate text-[9px] text-white">
                                    {attachment.name}
                                </span>

                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 shrink-0 text-white" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                    <circle cx="12" cy="12" r="2.5" />
                                </svg>
                            </div>
                        </>
                    ) : (
                        <div className="flex w-[260px] items-center gap-2 px-2.5 py-2">
                            {getFileIcon(attachment.name)}
                            <span className="truncate text-[10px] text-gray-700">
                                {attachment.name}
                            </span>
                        </div>
                    )}
                </button>
            );
        }

        if (isViewableAttachment) {
            return (
                <div className="flex w-full max-w-[280px] items-center gap-1 rounded-md border border-gray-200 bg-white transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5">
                    <button
                        type="button"
                        disabled={isLoadingFile || isDeletingMessage}
                        onClick={handleOpenFilePreview}
                        className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left disabled:cursor-not-allowed disabled:opacity-70"
                        title={`View ${attachment.name}`}
                    >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-50">
                            {isLoadingFile ? (
                                <Spinner size={14} />
                            ) : (
                                getFileIcon(attachment.name)
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-[10px] font-medium text-gray-700">
                                {attachment.name}
                            </p>
                            <p className="mt-0.5 text-[8px] text-gray-400">
                                {isLoadingFile ? 'Opening...' : 'Click to view'}
                            </p>
                        </div>
                    </button>

                    <div className="flex shrink-0 items-center gap-0.5 pr-1">
                        <button
                            type="button"
                            disabled={isDownloadingAttachment || isDeletingMessage || isLoadingFile}
                            onClick={handleDownloadAttachment}
                            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-[#186cb2]/10 hover:text-[#186cb2] disabled:cursor-not-allowed disabled:opacity-40"
                            title={`Download ${attachment.name}`}
                            aria-label={`Download ${attachment.name}`}
                        >
                            {isDownloadingAttachment ? (
                                <Spinner size={13} />
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m7 10 5 5 5-5" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 21h14" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            disabled={ isLoadingFile || isDeletingMessage }
                            onClick={handleOpenFilePreview}
                            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-[#186cb2]/10 hover:text-[#186cb2] disabled:cursor-not-allowed disabled:opacity-40"
                            title={`View ${attachment.name}`}
                            aria-label={`View ${attachment.name}`}
                        >
                            {isLoadingFile ? (
                                <Spinner size={13} />
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                    <circle cx="12" cy="12" r="2.5" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <button
                type="button"
                disabled={ isDownloadingAttachment || isDeletingMessage }
                onClick={handleDownloadAttachment}
                className="flex w-full max-w-[260px] items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:opacity-70"
                title={`Download ${attachment.name}`}
            >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-gray-50">
                    {isDownloadingAttachment ? (
                        <Spinner size={14} />
                    ) : (
                        getFileIcon(attachment.name)
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-[10px] font-medium text-gray-700">
                        {attachment.name}
                    </p>
                    <p className="mt-0.5 text-[8px] text-gray-400">
                        {isDownloadingAttachment ? 'Downloading...' : 'Click to download'}
                    </p>
                </div>

                {!isDownloadingAttachment && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 shrink-0 text-gray-400" >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="m7 10 5 5 5-5" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 21h14" />
                    </svg>
                )}
            </button>
        );
    };

    return (
        <div className={`flex w-full ${styleDiv}`}>
            <div className={`flex max-w-[90%] items-start gap-1 sm:max-w-[80%] md:max-w-[70%] ${ type === 'outgoing' ? 'flex-row-reverse' : '' }`} >
                <div className="flex w-7 shrink-0 flex-col items-center">
                    <div className="h-3" />
                    {type === 'incoming' && (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#186cb2] text-[10px] font-semibold uppercase text-white">
                            {msg.commenter_name?.[0] ?? '?'}
                        </div>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <MessageHeader
                        type={type}
                        commenterName={msg.commenter_name}
                        formattedCreatedAt={formattedCreatedAt}
                        isEdited={isEdited}
                    />
                    <div
                        ref={ref}
                        className={`flex items-start ${
                            type === 'outgoing'
                                ? 'justify-end'
                                : 'justify-start'
                        }`}
                    >
                        <div className="min-w-0 max-w-full">
                            {isDeleteMessage ? (
                                <div className="relative max-w-full rounded-lg rounded-tl-sm bg-gray-100 px-3 py-1.5 text-xs text-gray-400 shadow-sm">
                                    <p className="italic text-[10px]">
                                        This message has been deleted.
                                    </p>
                                    {isDeletingMessage && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
                                            <Spinner size={14} />
                                            <p className="ml-1 text-[10px] text-gray-500">
                                                Deleting...
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : hasMessageAndAttachment ? (
                                <div
                                    className={`relative max-w-full rounded-lg px-2 py-2 text-xs text-gray-800 shadow-sm ${
                                        type === 'outgoing'
                                            ? 'rounded-tr-sm'
                                            : 'rounded-tl-sm'
                                    } ${messageBgColor}`}
                                >
                                    <p className="break-words px-1 py-0.5">
                                        {msg.comment_text}
                                    </p>

                                    <div className="mt-1 overflow-hidden rounded-md">
                                        {renderAttachment()}
                                    </div>

                                    {isDeletingMessage && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
                                            <Spinner size={14} />

                                            <p className="ml-1 text-[10px] text-gray-500">
                                                Deleting...
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : hasMessage ? (
                                <div
                                    className={`${messageBgColor} relative max-w-full rounded-lg px-3 py-1.5 text-xs text-gray-800 shadow-sm ${
                                        type === 'outgoing'
                                            ? 'rounded-tr-sm'
                                            : 'rounded-tl-sm'
                                    }`}
                                >
                                    <p className="break-words">
                                        {msg.comment_text}
                                    </p>

                                    {isDeletingMessage && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
                                            <Spinner size={14} />

                                            <p className="ml-1 text-[10px] text-gray-500">
                                                Deleting...
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="relative max-w-full">
                                    {renderAttachment()}

                                    {isDeletingMessage && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
                                            <Spinner size={14} />

                                            <p className="ml-1 text-[10px] text-gray-500">
                                                Deleting...
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {!isDeleteMessage &&
                            type === 'outgoing' && (
                                <>
                                    <div className="relative ml-0.5 shrink-0">
                                        <button
                                            type="button"
                                            disabled={isDeletingMessage}
                                            onClick={onToggle}
                                            title="Message options"
                                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            <PepiconsPopDotsY
                                                color="#7f7f7f"
                                                height={15}
                                                width={15}
                                            />
                                        </button>
                                    </div>

                                    {isActive && (
                                        <div
                                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4 backdrop-blur-[2px]"
                                            onClick={onToggle}
                                        >
                                            <div
                                                className="w-full max-w-[280px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <div className="border-b border-gray-100 px-4 py-3">
                                                    <p className="text-xs font-semibold text-gray-800">
                                                        Message Options
                                                    </p>

                                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                                        Choose an action for this message
                                                    </p>
                                                </div>

                                                <div className="p-2">
                                                    {hasMessage && (
                                                        <button
                                                            type="button"
                                                            title="Edit message"
                                                            onClick={handleEdit}
                                                            disabled={isDeletingMessage}
                                                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs text-gray-700 transition hover:bg-[#186cb2]/5 hover:text-[#186cb2] disabled:cursor-not-allowed disabled:opacity-40"
                                                        >
                                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13l-2.68.805a4.5 4.5 0 0 1 1.13-1.897l8.922-8.941Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125 16.875 4.5" /></svg>
                                                            </span>

                                                            <span className="flex-1">
                                                                <span className="block font-medium">
                                                                    Edit message
                                                                </span>

                                                                <span className="mt-0.5 block text-[9px] text-gray-400">
                                                                    Modify the message content
                                                                </span>
                                                            </span>
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        title="Delete message"
                                                        onClick={handleDelete}
                                                        disabled={isDeletingMessage}
                                                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs text-gray-700 transition hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40 ${
                                                            hasMessage
                                                                ? 'mt-1'
                                                                : ''
                                                        }`}
                                                    >
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                                                        </span>
                                                        <span className="flex-1">
                                                            <span className="block font-medium">
                                                                Delete message
                                                            </span>
                                                            <span className="mt-0.5 block text-[9px] text-gray-400">
                                                                Remove this message from the chat
                                                            </span>
                                                        </span>
                                                    </button>
                                                </div>
                                                <div className="border-t border-gray-100 px-2 py-2">
                                                    <button
                                                        type="button"
                                                        onClick={onToggle}
                                                        disabled={isDeletingMessage}
                                                        className="w-full rounded-lg px-3 py-2 text-[10px] font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                    </div>
                    <div className={`mt-0.5 flex ${ type === 'outgoing' ? 'justify-end' : 'justify-start' }`} >
                        <span className="max-w-[180px] truncate text-[9px] italic text-gray-400">
                            {msg.department_name}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}